if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      const restoredOptionSelection = this.restoreOptionSelectionFromURL();
      if (this.disableSelectedVariantDefault && !restoredOptionSelection) {
        this.handleDisableSelectedVariantDefault();
      }
      if (restoredOptionSelection && (!this.currentVariant ||
        String(this.currentVariant.id) !== String(this.getVariantIdForSelectedOptions(this.variantPicker)))) {
        this.setUnavailable();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();

      const explicitVariantId = new URLSearchParams(window.location.search).get('variant');
      const hasExplicitSelectedVariant = this.viewMode === 'main-product' &&
        /^[1-9]\d*$/.test(explicitVariantId || '') &&
        String(this.currentVariant?.id) === explicitVariantId;

      if (this.currentVariant && (!this.showFeaturedMedia || hasExplicitSelectedVariant)) {
        this.updateMedia(this.currentVariant);
      }
    }

    getVariantGroupImagesData() {
      return (
        JSON.parse(
          this.querySelector('#variantGroup[type="application/json"]')
            .textContent
        ) || {}
      );
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const picker = target.closest('variant-picker') || this.variantPicker;
      const requiredOptionCount = picker?.querySelectorAll('[data-picker-field]').length || 0;
      if (requiredOptionCount > 0 && selectedOptionValues.length < requiredOptionCount) {
        this.failClosedSelection();
        return;
      }

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      const explicitVariantId = shouldSwapProduct ? null : this.getVariantIdForSelectedOptions(picker);

      if (!shouldSwapProduct && requiredOptionCount > 0 && !explicitVariantId) {
        this.failClosedSelection();
        return;
      }
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      // The picker already shows the new selection, but the buy block still belongs to the previous variant until the server
      // answers. Nothing may act on that previous variant in the meantime (Add to cart, Buy it now, Secure Invoice).
      this.holdPurchaseActions();
      if (!shouldSwapProduct) this.updateURL(productUrl, null);

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, explicitVariantId),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    failClosedSelection() {
      this.abortController?.abort();
      this.abortController = undefined;
      this.pendingRequestUrl = null;
      this.currentVariant = null;
      this.updateVariantInputs(null);
      this.setUnavailable();
      this.updateURL(this.dataset.url, null);
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      const requestController = new AbortController();
      this.abortController = requestController;
      const isCurrentRequest = () => this.abortController === requestController && !requestController.signal.aborted;
      this.refreshCommitted = false;

      fetch(requestUrl, { signal: requestController.signal })
        .then((response) => {
          // An error page is not a product page: treat it as a failed refresh instead of parsing it.
          if (!response.ok) throw new Error(`Option refresh failed (HTTP ${response.status})`);
          return response.text();
        })
        .then((responseText) => {
          if (!isCurrentRequest()) return;
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          if (!isCurrentRequest()) return;
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (!isCurrentRequest()) return;
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
            // A refresh that did not complete leaves the previous variant on screen. Fail closed: it stays unpurchasable until a
            // later selection is confirmed by the server. (A refresh that already committed is never undone by a stray error.)
            if (!this.refreshCommitted && this.isConnected) this.failClosedSelection();
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
      const requestUrl = new URL(url, window.location.origin);

      if (variantId) {
        requestUrl.searchParams.set('variant', variantId);
        requestUrl.searchParams.delete('option_values');
      } else if (optionValues.length) {
        requestUrl.searchParams.set('option_values', optionValues.join(','));
        requestUrl.searchParams.delete('variant');
      }
      if (!shouldFetchFullPage) requestUrl.searchParams.set('section_id', this.sectionId);

      return requestUrl.toString();
    }

    getVariantIdForSelectedOptions(picker) {
      if (!picker) return null;

      const selectedOptions = Array.from(picker.querySelectorAll('[data-picker-field]')).map((field) => {
        const select = field.querySelector('select');
        if (select) return select.value || '';
        return field.querySelector('input[type="radio"]:checked')?.value || '';
      });
      if (selectedOptions.some((value) => !value)) return null;

      const policyNode = this.querySelector('[data-one-luxury-purchase-policy]') ||
        Array.from(document.querySelectorAll('[data-one-luxury-purchase-policy]'))
          .find((node) => String(node.dataset.productId || '') === String(this.productId));
      if (!policyNode) return null;

      try {
        const policy = JSON.parse(policyNode.textContent);
        const variant = policy.variants?.find((item) => (
          Array.isArray(item.options) &&
          item.options.length === selectedOptions.length &&
          item.options.every((value, index) => value === selectedOptions[index])
        ));
        return variant?.id || null;
      } catch (error) {
        console.error('1Luxury purchase policy could not resolve selected options.', error);
        return null;
      }
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);
        const selectedOptionValues = this.getSelectedOptionValueIds();

        this.pickupAvailability?.update(variant);
        this.updateURL(productUrl, variant?.id, selectedOptionValues);

        if (!variant) {
          // No variant for this selection: forget the previous one, not only the Add to cart label.
          this.currentVariant = null;
          this.updateVariantInputs(null);
          this.setUnavailable();
          return;
        }

        this.updateOptionValues(html);
        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination('PaymentTerms');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        // Read the server-rendered add-to-cart state BEFORE the buy-buttons block is moved into the live page below.
        // Element.replaceWith() moves the node out of `html`, so a later html.getElementById() would return null.
        const addButtonSource = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const addButtonDisabled = addButtonSource ? addButtonSource.hasAttribute('disabled') : null;

        const buyButtonsSource = html.querySelector('.main-product__block-buy_buttons');
        const buyButtonsDestination = this.querySelector('.main-product__block-buy_buttons');
        if (buyButtonsSource && buyButtonsDestination) {
          // Shopify's accelerated-checkout element binds to its owning variant
          // input when dynamically inserted. Commit the exact canonical variant
          // before insertion so the native control never initializes against the
          // server-rendered disabled placeholder.
          const sourceVariantInput = buyButtonsSource.querySelector(
            'input[name="id"][data-one-luxury-canonical-variant-input="true"]'
          );
          if (sourceVariantInput) {
            const resolvedVariantId = String(variant.id);
            sourceVariantInput.value = resolvedVariantId;
            sourceVariantInput.setAttribute('value', resolvedVariantId);
            sourceVariantInput.dataset.selectedVariant = resolvedVariantId;
            sourceVariantInput.disabled = false;
          }
          buyButtonsDestination.replaceWith(buyButtonsSource);
        }

        const stickySource = html.querySelector('.m-sticky-addtocart');
        const stickyDestination = this.querySelector('.m-sticky-addtocart');
        if (stickySource && stickyDestination) {
          stickyDestination.replaceWith(stickySource);
        } else if (!stickySource && stickyDestination) {
          stickyDestination.remove();
        }

        const availabilitySource = html.querySelector(
          ".one-luxury-availability--product[data-one-luxury-availability]"
        );
        const availabilityDestination = this.querySelector(
          ".one-luxury-availability--product[data-one-luxury-availability]"
        );
        if (availabilitySource && availabilityDestination) {
          availabilityDestination.replaceWith(availabilitySource);
          this.dataset.availabilityMode = availabilitySource.dataset.availabilityMode || "";
        }

        // The explicit-selection flow can replace a placeholder buy block with
        // the real product form. Update the variant only after that replacement
        // so the newly inserted cart input receives the selected variant ID.
        this.updateVariantInputs(variant.id);

        // Easify Product Options disables accelerated-checkout pointer handling
        // before its initial-page integration pass. A native control inserted by
        // this section update is outside that pass, so release only the exact
        // canonical form's Shopify element after the resolved variant is bound.
        // The native element remains the sole checkout action.
        const canonicalVariantInput = this.querySelector(
          'input[name="id"][data-one-luxury-canonical-variant-input="true"]'
        );
        const canonicalAcceleratedCheckout = canonicalVariantInput?.form?.querySelector(
          'shopify-accelerated-checkout'
        );
        if (canonicalAcceleratedCheckout) {
          canonicalAcceleratedCheckout.style.pointerEvents = 'auto';
        }

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        // The server's state decides, but an unavailable variant is never enabled, and if no button was found
        // in the response the selection fails closed. (purchase-integrity.js still re-checks every form.)
        this.toggleAddButton(
          addButtonDisabled === null ? true : (addButtonDisabled || !variant.available),
          window.MinimogStrings.soldOut
        );

        this.currentVariant = variant;
        this.refreshCommitted = true;
        this.syncInvoiceLink(variant);

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
        this.refreshCommitted = true;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    getSelectedOptionValueIds() {
      return Array.from(this.variantPicker?.querySelectorAll('[data-picker-field]') || []).map((field) => {
        const select = field.querySelector('select');
        const selected = select
          ? Array.from(select.options).find((option) => option.selected && !option.disabled)
          : field.querySelector('input[type="radio"]:checked');
        return selected?.dataset.optionValueId || '';
      });
    }

    getOptionValueElements(ids) {
      const fields = Array.from(this.variantPicker?.querySelectorAll('[data-picker-field]') || []);
      if (!Array.isArray(ids) || !fields.length || ids.length !== fields.length ||
        ids.some((id) => !/^[1-9]\d*$/.test(String(id)))) return null;
      const options = fields.map((field, index) => Array.from(
        field.querySelectorAll('input[type="radio"][data-option-value-id], option[data-option-value-id]')
      ).find((option) => option.dataset.optionValueId === String(ids[index])));
      return options.every(Boolean) ? { fields, options } : null;
    }

    restoreOptionSelectionFromURL() {
      if (this.viewMode !== 'main-product') return false;
      const params = new URLSearchParams(window.location.search);
      if (params.has('variant')) return false;
      const selection = this.getOptionValueElements((params.get('option_values') || '').split(','));
      if (!selection) return false;
      selection.fields.forEach((field, index) => {
        const option = selection.options[index];
        const select = field.querySelector('select');
        if (select) {
          Array.from(select.options).forEach((item) => { item.selected = item === option; });
          select.value = option.value;
        } else {
          field.querySelectorAll('input[type="radio"]').forEach((item) => { item.checked = item === option; });
        }
        field.dataset.selectedValue = option.value;
        const label = field.querySelector('.option-label--selected');
        if (label) label.textContent = option.value;
      });
      return true;
    }

    updateURL(url, variantId, optionValues = this.getSelectedOptionValueIds()) {
      if (this.dataset.updateUrl === 'false' || this.viewMode !== 'main-product') return;
      try {
        const currentUrl = new URL(window.location.href);
        const productUrl = new URL(url, currentUrl);
        if (productUrl.origin !== currentUrl.origin) return;
        const sameProduct = productUrl.pathname === new URL(this.dataset.url, currentUrl).pathname;
        const nextUrl = new URL(currentUrl);
        if (!sameProduct) nextUrl.pathname = productUrl.pathname;
        productUrl.searchParams.forEach((value, key) => nextUrl.searchParams.set(key, value));
        if (productUrl.hash) nextUrl.hash = productUrl.hash;
        nextUrl.searchParams.delete('variant');
        nextUrl.searchParams.delete('option_values');
        if (this.enableHistoryState) {
          if (variantId) nextUrl.searchParams.set('variant', String(variantId));
          else if (sameProduct && this.getOptionValueElements(optionValues)) {
            nextUrl.searchParams.set('option_values', optionValues.join(','));
          }
        }
        window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      } catch (error) {
        console.error('Product selection URL could not be updated.', error);
      }
    }

    updateVariantInputs(variantId) {
      const resolvedVariantId = variantId == null ? '' : String(variantId);
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;

          const isCanonicalVariantInput = input.dataset.oneLuxuryCanonicalVariantInput === 'true';
          const commitCanonicalVariant = () => {
            if (!isCanonicalVariantInput) return;
            input.value = resolvedVariantId;
            input.setAttribute('value', resolvedVariantId);
            input.dataset.selectedVariant = resolvedVariantId;
            input.disabled = !resolvedVariantId;
          };

          input.value = resolvedVariantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          commitCanonicalVariant();
          if (isCanonicalVariantInput) {
            queueMicrotask(commitCanonicalVariant);
            requestAnimationFrame(commitCanonicalVariant);
          }
          const paymentTerms = productForm.querySelector('shopify-payment-terms');
          if (paymentTerms) paymentTerms.setAttribute('variant-id', resolvedVariantId);
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
      }
    }

    // Everything that could still act on a previously resolved variant stops being actionable. The server-rendered block that replaces
    // these elements after a confirmed variant restores them; if no confirmed variant arrives they simply stay held (fail closed).
    holdPurchaseActions() {
      // The resolved selection is no longer known while a new one is pending: anything that reads currentVariant (the localization
      // form's return target, quantity rules) must see "none", exactly like the purchase controls held below.
      this.currentVariant = null;
      this.toggleAddButton(true); // transient hold: keep the label, only the state changes
      this.updateVariantInputs(null);

      // Accelerated checkout (Buy it now / PayPal / Venmo): no pointer, keyboard or assistive-technology activation.
      this.querySelectorAll('shopify-accelerated-checkout, .shopify-payment-button').forEach((host) => {
        host.setAttribute('inert', '');
        host.setAttribute('aria-disabled', 'true');
        host.style.pointerEvents = 'none';
      });

      // Secure Invoice link: without an href it can neither be followed, copied nor opened in a new tab.
      this.querySelectorAll('[data-one-luxury-secure-invoice]').forEach((link) => this.holdInvoiceLink(link));

      // Sticky add to cart (exact-variant forms only exist for a few products).
      this.querySelectorAll('.m-sticky-addtocart button[name="add"]').forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
      });
      this.querySelectorAll('.m-sticky-addtocart [name="id"][data-one-luxury-canonical-sticky-variant-input="true"]').forEach((input) => {
        input.value = '';
        input.disabled = true;
      });

      // The purchase-integrity guard falls back to the availability block's variant id when the form has none. That id still names the
      // previous variant, so clear it: with no id the guard keeps every purchase control disabled.
      this.querySelectorAll('[data-one-luxury-availability]').forEach((node) => {
        node.dataset.availabilityVariantId = '';
      });
    }

    holdInvoiceLink(link) {
      const href = link.getAttribute('href');
      if (href) link.dataset.heldHref = href;
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
      link.style.pointerEvents = 'none';
      link.style.opacity = '0.5';
    }

    // After a confirmed variant: the invoice link must name exactly that variant, otherwise it stays held.
    syncInvoiceLink(variant) {
      this.querySelectorAll('[data-one-luxury-secure-invoice]').forEach((link) => {
        let linked = null;
        try {
          linked = new URL(link.getAttribute('href') || '', window.location.origin).searchParams.get('variant');
        } catch (error) {
          linked = null;
        }
        if (linked !== String(variant.id)) this.holdInvoiceLink(link);
      });
    }

    setUnavailable() {
      this.holdPurchaseActions();
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      if (this.enableVariantGroupImages && this.variantGroupImagesData.enable) {
        this.handleVariantGroupImage(variant);

        if (this.productMedia.slider) {
          this.productMedia.slider.updateSlides();
          this.productMedia.navSlider.updateSlides();

          this.productMedia.slider.slideTo(0);
          this.productMedia.navSlider.slideTo(0);

          this.productMedia.handleSlideChange();
        }
      } else {
        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    handleVariantGroupImage(variant) {
      const selectedVariantData = this.variantGroupImagesData.mapping.find(
        (item) => Number(item.id) === variant.id
      );

      const selectedMedias = (initialItems) => {
        const selectedVariantMedias = Array.from(initialItems)
          .map((media) => {
            const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
            const mediaType = media.dataset.mediaType;
            if (index !== -1 || mediaType !== 'image') {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from(initialItems).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = Object.values(selectedVariantData.media)
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

        return Array.from([...orderedMedias, ...remainingMedias]).map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      selectedMedias(this.initialMedias).forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.remove("m-col-span-2");
          if (index % 3 === 0) {
            media.classList.add("m-col-span-2");
          }
        }
        mediaWrapper.append(media);
      });
      selectedMedias(this.initialThumbs).forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
        thumbWrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      if (!this.currentVariant?.id) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}
